import { Component, EventEmitter, Output, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SharedDataService } from '../../../services/shared-data.service';
import { AuthService } from '../../../services/auth.service';
import { AuthUser } from '../../../models/auth.models';
import { Log, IShortLogger } from '../../../services/logger.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationsService } from '../../../services/home-services/notifications.service';
import { ErrorHandlerService } from '../../../decoration-base/error-handler.service';

@Component({
  selector: 'app-nav-header',
  templateUrl: './nav-header.component.html',
  styleUrl: './nav-header.component.css'
})
export class NavHeaderComponent implements OnInit, OnDestroy {
  private readonly logger: IShortLogger<NavHeaderComponent> = Log.create(NavHeaderComponent);

  @Output() toggleSidebar = new EventEmitter<void>();
  @Input() sidebarCollapsed = false;

  currentUser: AuthUser | null = null;
  selectedMenu: string | undefined = '';
  unreadCount = 0;

  private userId = '';
  private authSubscription: Subscription = new Subscription();
  private menuSubscription: Subscription = new Subscription();
  private notificationSubscription?: Subscription;

  constructor(
    private sharedService: SharedDataService,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private notificationsService: NotificationsService,
    private errorHandler: ErrorHandlerService
  ) { }

  async ngOnInit() {
    this.authSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
    this.menuSubscription = this.sharedService.selectedMenu$.subscribe(menu => {
      this.selectedMenu = menu;
    });
    this.userId = localStorage.getItem('userId') || '';
    await this.connect();
  }

  async ngOnDestroy() {
    this.authSubscription.unsubscribe();
    this.menuSubscription.unsubscribe();
    await this.disconnect();
  }

  onBurgerClick(): void {
    this.toggleSidebar.emit();
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.snackBar.open('You have been logged out successfully', undefined, { duration: 3000 });
        this.router.navigate(['/login']);
      },
      error: (error) => {
        this.logger.e('Logout error:', error);
        this.router.navigate(['/login']);
      }
    });
  }

  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  getUserDisplayName(): string {
    return this.currentUser?.name || 'User';
  }

  getUserInitials(): string {
    if (!this.currentUser?.name) return 'U';
    const names = this.currentUser.name.split(' ');
    return names.length >= 2
      ? (names[0][0] + names[1][0]).toUpperCase()
      : this.currentUser.name[0].toUpperCase();
  }

  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  hasRole(role: string): boolean {
    return this.authService.hasRole(role);
  }

  private async connect(): Promise<void> {
    if (!this.userId) return;
    await this.notificationsService.joinUserGroup(this.userId);
    this.notificationSubscription = this.notificationsService.unreadCount$
      .subscribe(count => { this.unreadCount = count; });
    this.notificationsService.loadNotificationsForUser(this.userId);
  }

  private async disconnect(): Promise<void> {
    this.notificationSubscription?.unsubscribe();
    await this.notificationsService.disconnect(this.userId);
  }
}
